import { motion } from "framer-motion";
import { QrCode, Smartphone, BarChart3 } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";

export function StepsSection() {
  const { language } = useLanguage();

  const content = {
    fr: {
      title: "Comment ça marche ?",
      subtitle: "3 étapes simples pour centraliser vos avis",
      step1Title: "Affichez votre QR Code",
      step1Desc: "Générez votre QR Code universel et téléchargez votre support (chevalet, vitrophanie ou carte) prêt à imprimer.",
      step2Title: "Le client choisit sa plateforme",
      step2Desc: "Vos clients scannent le code et choisissent où laisser leur avis : Google, Facebook, Yelp, TripAdvisor ou Trustpilot.",
      step3Title: "Centralisez et analysez",
      step3Desc: "Tous vos avis remontent dans un tableau de bord unique. Répondez, analysez et pilotez votre réputation.",
    },
    en: {
      title: "How It Works",
      subtitle: "3 simple steps to centralize your reviews",
      step1Title: "Display Your QR Code",
      step1Desc: "Generate your universal QR Code and download your ready-to-print display (table tent, window sticker or card).",
      step2Title: "Customer Picks Their Platform",
      step2Desc: "Customers scan the code and choose where to leave their review: Google, Facebook, Yelp, TripAdvisor, or Trustpilot.",
      step3Title: "Centralize & Analyze",
      step3Desc: "All your reviews flow into one dashboard. Respond, analyze, and manage your reputation from a single place.",
    },
  };

  const t = content[language];

  const steps = [
    {
      icon: QrCode,
      title: t.step1Title,
      description: t.step1Desc,
      color: "bg-primary/10",
      iconColor: "text-primary",
    },
    {
      icon: Smartphone,
      title: t.step2Title,
      description: t.step2Desc,
      color: "bg-success/10",
      iconColor: "text-success",
    },
    {
      icon: BarChart3,
      title: t.step3Title,
      description: t.step3Desc,
      color: "bg-warning/10",
      iconColor: "text-warning",
    },
  ];

  return (
    <section className="container mx-auto px-4 py-20 bg-slate-50/50">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center mb-16"
      >
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
          {t.title}
        </h2>
        <p className="text-muted-foreground max-w-xl mx-auto">
          {t.subtitle}
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
        {steps.map((step, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: index * 0.15 }}
            className="relative"
          >
            {/* Step number */}
            <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold text-lg flex items-center justify-center shadow-lg z-10">
              {index + 1}
            </div>

            {/* Card */}
            <div className="bg-card border border-slate-100 rounded-2xl p-8 pt-10 h-full shadow-sm hover:shadow-md transition-shadow">
              <div className={`w-14 h-14 rounded-xl ${step.color} flex items-center justify-center mb-6`}>
                <step.icon className={step.iconColor} size={28} />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">
                {step.title}
              </h3>
              <p className="text-muted-foreground">
                {step.description}
              </p>
            </div>

            {/* Connector line (hidden on mobile and last item) */}
            {index < 2 && (
              <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-slate-200" />
            )}
          </motion.div>
        ))}
      </div>
    </section>
  );
}